import type { Item } from './components'
import type {
  ItemPriceRange,
  ItemsToConsider,
  OPERATORS,
  ZipCodeRange,
} from './promotion'

export type OrderValue = {
  min?: number
  max?: number
  itemsToConsider: ItemsToConsider
}

export type UTM = {
  utmSource: string[]
  utmCampaign: string[]
}

export type UTMICampaign = {
  utmiCampaign: string[]
}

export enum ListPriceKeys {
  NOT_DEFINED = 'notDefined',
  DEFINED = 'defined',
}

export type ListPrice = undefined | ListPriceKeys

export enum ClusterOperators {
  ALL = 'all',
  ANY = 'any',
}

export type Cluster = {
  expressions: string[]
  operator: ClusterOperators
}

export type BIN = string[]

export type ShippingMethods = Item[]

export type ShippingLocation = {
  shippingCountries: string[]
  zipCodeRanges: ZipCodeRange[]
}

export type MinInstallments = {
  min: number
  max: undefined
}
export type MaxInstallments = {
  min: undefined
  max: number
}

export type InstallmentsBetween = {
  min: number
  max: number
}

export type Installments =
  | MinInstallments
  | MaxInstallments
  | InstallmentsBetween
  | undefined

export type Affiliates =
  | {
      affiliates: Item[]
      operator: OPERATORS.DIFFERENT | OPERATORS.EQUALS
    }
  | undefined

export type MarketingTags = {
  operator: OPERATORS.EQUALS | OPERATORS.DIFFERENT
  tags: string[]
}

export enum FirstPurchaseLogin {
  OPTIONAL = 'optional',
  REQUIRED = 'required',
}

export type FirstPurchase = {
  login: FirstPurchaseLogin
}

export enum SubscriptionType {
  ALL = 'all',
  FIRST = 'first',
  RECURRING = 'recurring',
}

export enum SubscriptionFrequencyUnit {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export type SubscriptionFrequency = {
  unit: SubscriptionFrequencyUnit
  value: number
}

export enum SubscriptionCycleOperators {
  EQUALS = 'equals',
  GREATER_THAN = 'greaterThan',
  LESS_THAN = 'lessThan',
  RANGE = 'range',
}

export type SubscriptionCycle =
  | {
      operator:
        | SubscriptionCycleOperators.EQUALS
        | SubscriptionCycleOperators.GREATER_THAN
        | SubscriptionCycleOperators.LESS_THAN
      value: number
    }
  | {
      operator: SubscriptionCycleOperators.RANGE
      min: number
      max: number
    }

export type Subscription = {
  type: SubscriptionType
  filter: {
    frequency: SubscriptionFrequency | undefined
    cycle: SubscriptionCycle | undefined
  }
}

export enum CONDITION {
  PAYMENT_METHODS = 'paymentMethods',
  ORDER_VALUE = 'orderValue',
  UTM = 'utm',
  CLUSTER = 'cluster',
  BIN = 'bin',
  SHIPPING_METHODS = 'shippingMethods',
  SHIPPING_LOCATION = 'shippingLocation',
  LIST_PRICE = 'listPrice',
  INSTALLMENTS = 'installments',
  ITEM_PRICE_RANGE = 'itemPriceRange',
  AFFILIATES = 'affiliates',
  HISTORICAL_PURCHASE_VALUE = 'historicalPurchaseValue',
  MARKETING_TAGS = 'marketingTags',
  FIRST_PURCHASE = 'firstPurchase',
  SUBSCRIPTION = 'subscription',
  UTMI_CAMPAIGN = 'utmiCampaign',
}

export interface Conditions {
  [CONDITION.PAYMENT_METHODS]?: Item[]
  [CONDITION.ORDER_VALUE]?: OrderValue
  [CONDITION.UTM]?: UTM
  [CONDITION.CLUSTER]?: Cluster
  [CONDITION.BIN]?: BIN
  [CONDITION.SHIPPING_METHODS]?: ShippingMethods
  [CONDITION.SHIPPING_LOCATION]?: ShippingLocation
  [CONDITION.LIST_PRICE]?: ListPrice
  [CONDITION.INSTALLMENTS]?: Installments
  [CONDITION.ITEM_PRICE_RANGE]?: ItemPriceRange
  [CONDITION.AFFILIATES]?: Affiliates
  [CONDITION.HISTORICAL_PURCHASE_VALUE]?: number
  [CONDITION.MARKETING_TAGS]?: MarketingTags
  [CONDITION.FIRST_PURCHASE]?: FirstPurchase
  [CONDITION.SUBSCRIPTION]?: Subscription
  [CONDITION.UTMI_CAMPAIGN]?: UTMICampaign
}
